import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import styles from './styles.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { TagAPI } from '@apis/bff-api/tag';

type DeleteTagDialogProps = {
    studyRef: string;
    tagId: number | undefined;
    activeInstances: number | undefined;

    onClose: () => void;
    onSubmit: () => void;
};

interface IDeleteTagForm {
    reasonForChangeMessage: string;
}

const COMPONENT_NAME = 'delete-tag-dialog';

const DeleteTagDialog = ({ studyRef, activeInstances, tagId, onSubmit, onClose }: DeleteTagDialogProps) => {
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });

    const { trigger: deleteTag, isMutating: isDeletingTag } = TagAPI.useDeleteTag(studyRef);

    const {
        control: deleteTagControl,
        handleSubmit: handleDeleteTagSubmit,
        formState: { isDirty: deleteTagFormIsDirty, isValid: deleteTagFormIsValid, isSubmitting },
    } = useForm<IDeleteTagForm>({
        mode: 'all',
    });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: deleteTagFormIsDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const successfulDelete = (success: boolean) => {
        if (success) {
            ShowSuccess(t('actionsSuccessfulToast'));
            onSubmit();
        } else {
            ShowError(t('errorMessage'));
            onClose();
        }
    };

    const onCloseDeleteHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="delete-tag"
            onSubmit={async () =>
                handleDeleteTagSubmit(async (values: IDeleteTagForm) => {
                    if (!values.reasonForChangeMessage || values.reasonForChangeMessage.trim().length === 0 || !tagId) {
                        return;
                    }

                    try {
                        const result = await deleteTag({
                            id: tagId,
                            reason: values.reasonForChangeMessage.trim(),
                        });

                        if (result) {
                            successfulDelete(true);
                        } else {
                            successfulDelete(false);
                        }
                    } catch {
                        successfulDelete(false);
                    }
                })()
            }
            onClose={onCloseDeleteHandler}
            title={mtkTranslation('deleteTagDialog.title')}
            width={600}
            height={400}
            primaryBtnDisabled={!deleteTagFormIsDirty || !deleteTagFormIsValid || isSubmitting || isDeletingTag}
            secondaryBtnDisabled={isSubmitting || isDeletingTag}
        >
            <div className={cn('k-w-full', 'k-h-full', 'k-d-flex-col')}>
                <div className="k-d-grid k-pb-2">
                    <InfoField
                        message={`${t('sm:monitoringToolkit.deleteTagDialog.message', {
                            num: activeInstances,
                            times: activeInstances === 1 ? 'time' : 'times',
                        })}`}
                        layout="stretch"
                        type="Warning"
                    />
                </div>
                <TextAreaController
                    control={deleteTagControl}
                    name="reasonForChangeMessage"
                    data-testid="delete-tag-reason-for-change-message"
                    fillMode="solid"
                    maxLength={4000}
                    required
                    label={mtkTranslation('deleteTagDialog.reasonForChange')}
                    className={styles.deleteReason}
                    readOnly={isSubmitting || isDeletingTag}
                    validateRules={{
                        nonEmpty: (value) => {
                            if (!(value as string).trim().length) {
                                return 'This field is required';
                            }
                            return true;
                        },
                    }}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteTagDialog;
